import { useState, useEffect, useMemo } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, ClipboardPlus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { FlhaListCard, type FlhaSummary } from "@/components/flha/flha-list-card";
import { useResumeFlha } from "@/hooks/use-resume-flha";
import { useFlhaLabel } from "@/hooks/use-flha-label";
import { useOfflineFlha } from "@/hooks/use-offline-flha";
import { useSession } from "@/lib/auth-client";
import type { FlhaDraft } from "@/lib/offline-db";
import { SubFlhaUploads } from "@/components/subs/sub-flha-uploads";

const myFlhasSearchSchema = z.object({
  status: z.string().optional(),
  q: z.string().optional(),
  page: z.number().int().positive().optional(),
});

export const Route = createFileRoute("/_authenticated/my-flhas")({
  validateSearch: myFlhasSearchSchema,
  component: MyFLHAsPage,
});

interface FlhaListResponse {
  data: FlhaSummary[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

const PAGE_SIZE = 20;

/** Convert a local draft to a FlhaSummary shape with sync status info */
function draftToSummary(
  d: FlhaDraft
): FlhaSummary & { syncStatus: "pending" | "synced"; isOffline: boolean } {
  return {
    id: d.id,
    projectName: d.data.projectName,
    status: d.status,
    workArea: d.data.workArea || null,
    shift: d.data.shift || null,
    createdAt: d.updatedAt,
    updatedAt: d.updatedAt,
    syncStatus: d.syncedAt ? "synced" : "pending",
    isOffline: true,
  };
}

function MyFLHAsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const label = useFlhaLabel();
  const statusFilter = search.status ?? "all";
  const searchQuery = search.q ?? "";
  const page = search.page ?? 1;

  const setStatusFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: value === "all" ? undefined : value,
        page: undefined,
      }),
      replace: true,
    });
  };
  const setSearchQuery = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        q: value ? value : undefined,
        page: undefined,
      }),
      replace: true,
    });
  };
  const setPage = (updater: number | ((p: number) => number)) => {
    const next = typeof updater === "function" ? updater(page) : updater;
    navigate({
      search: (prev) => ({
        ...prev,
        page: next === 1 ? undefined : next,
      }),
      replace: true,
    });
  };

  const { data: session } = useSession();
  const { listDrafts } = useOfflineFlha();
  const [localDrafts, setLocalDrafts] = useState<FlhaDraft[]>([]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["flha-list", statusFilter, page],
    queryFn: () =>
      apiGet<FlhaListResponse>(`/api/flha?status=${statusFilter}&limit=${PAGE_SIZE}&page=${page}`),
  });

  // Load local drafts from IndexedDB
  useEffect(() => {
    if (session?.user?.id) {
      listDrafts(session.user.id).then(setLocalDrafts);
    }
  }, [session?.user?.id, listDrafts]);

  const resumeFlha = useResumeFlha();

  const serverFlhas = data?.data || [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // Merge local offline drafts with server data
  const mergedFlhas = useMemo(() => {
    const serverIds = new Set(serverFlhas.map((f) => f.id));
    const offlineOnly = localDrafts
      .filter((d) => !d.serverId || !serverIds.has(d.serverId))
      .map(draftToSummary);
    const serverWithStatus = serverFlhas.map((f) => ({
      ...f,
      syncStatus: "synced" as const,
      isOffline: false,
    }));
    return [...offlineOnly, ...serverWithStatus];
  }, [serverFlhas, localDrafts]);

  // Client-side text search filter
  const filteredFlhas = useMemo(() => {
    if (!searchQuery.trim()) return mergedFlhas;
    const q = searchQuery.toLowerCase().trim();
    return mergedFlhas.filter((f) =>
      (f.projectName && f.projectName.toLowerCase().includes(q)) ||
      (f.workArea && f.workArea.toLowerCase().includes(q)) ||
      f.status.toLowerCase().includes(q) ||
      f.id.toLowerCase().includes(q)
    );
  }, [mergedFlhas, searchQuery]);

  // Sort rejected FLHAs to top, then offline-pending items
  const sortedFlhas = [...filteredFlhas].sort((a, b) => {
    if (a.status === "rejected" && b.status !== "rejected") return -1;
    if (a.status !== "rejected" && b.status === "rejected") return 1;
    if (a.isOffline && !b.isOffline) return -1;
    if (!a.isOffline && b.isOffline) return 1;
    return 0;
  });

  const handleResume = (flhaId: string) => {
    resumeFlha.mutate(flhaId);
  };

  const handleView = (flhaId: string) => {
    navigate({ to: "/flha/$flhaId", params: { flhaId } });
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold text-text-primary pt-2">{label.myLabel}</h1>

      {(session?.user as { role?: string } | undefined)?.role === "subcontractor" && <SubFlhaUploads canUpload />}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by project, work area..."
          className="pl-9 h-11"
        />
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
              statusFilter === filter.value
                ? "bg-paris-red text-white"
                : "bg-card text-text-secondary border border-border hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="py-3 px-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-5 w-14" />
                </div>
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-destructive">
              Failed to load FLHAs. Please try again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !isError && sortedFlhas.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              {searchQuery
                ? "No results found"
                : statusFilter === "all"
                  ? `No ${label.plural.toLowerCase()} yet`
                  : `No ${statusFilter} ${label.plural.toLowerCase()}`}
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1 mb-4">
              {searchQuery
                ? "Try a different search term."
                : statusFilter === "all"
                  ? "Start your first one to get going!"
                  : "Try a different filter."}
            </p>
            {statusFilter === "all" && !searchQuery && (
              <Link to="/flha/new-v2">
                <Button className="bg-paris-red hover:bg-paris-red-dark gap-2">
                  <ClipboardPlus className="h-4 w-4" />
                  {label.startNew}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* FLHA list */}
      {!isLoading && !isError && sortedFlhas.length > 0 && (
        <div className="space-y-2">
          {sortedFlhas.map((flha) => (
            <FlhaListCard
              key={flha.id}
              flha={flha}
              onResume={handleResume}
              onView={handleView}
              showActionNeeded
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && totalPages > 1 && !searchQuery && (
        <div className="flex items-center justify-between pt-2 pb-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Loading overlay when resuming */}
      {resumeFlha.isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <Card className="px-6 py-4">
            <p className="text-sm font-medium">Loading draft...</p>
          </Card>
        </div>
      )}
    </div>
  );
}
